import { RequestFileDto } from "../Schemas/RequestFileDto";
import { TaxonomyDto } from "../Schemas/TaxonomyDto";

export interface RequestUploadFileDto {
    fileId?: string;
    fileChunk: Uint8Array;
    fileName?: string;
    fileExtension?: string;
    size: number;
    taxonomyId?: number;
}


export interface RequestUploadFileWithTaxonomyDto extends RequestUploadFileDto {
    id: string | undefined;
    taxonomy: TaxonomyDto;
}

export interface GenerateUploadInput {
    requestId: string;
    requestTypeId: number;
    parameters: [];
    isUploadFinished: boolean;
    uploadSessionId: string | undefined;
    sendNotification: boolean;
    fileToUpload: RequestUploadFileWithTaxonomyDto;
}

export function CreateGenericChunkedUploader(
    uploadServiceInputGeneratorFn: (
        file: Record<string, any>,
        byteArray: Uint8Array,
        isEndOfFile: boolean
    ) => GenerateUploadInput,
    uploadService: (input: Record<string, any>) => Promise<Record<string, any>>,
    errorHandler: (file: Record<string, any>) => void,
    originalChunkSize: number,
    fnFileProgressUpdate: (file: Record<string, any>) => void,
    fnFileUploadCompletedEvent: (file: Record<string, any>) => void,
    maximumPixelSizeBeforeDownscale: number
) {
    const chunkSize = 1000; // First initial chunk should be small to probe the service, only 1000B

    function UploadFileFunction(filesToRead: RequestFileDto[]) {
        return new Promise((resolve, reject) => {
            const promises = filesToRead.map((file) => {
                return new Promise((resolveFile, _rejectFile) => {
                    file.fileDeferred = new Promise(
                        (resolveReadFile, _rejectReadFile) => {
                            ReadFile(file, resolveReadFile);
                        }
                    );
                    resolveFile(file.fileDeferred);
                });
            });
            Promise.all(promises)
                .then(() => {
                    resolve(filesToRead);
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    function UploadFile(
        input: Record<any, any>,
        uploadCompleteDeferred: (file: Record<string, any>) => void,
        endOfFile: boolean,
        file: Record<string, any>,
        seek: ((sessionId?: string) => void) | undefined,
        offset?: number
    ) {
        uploadService(input).then(
            function (result: Record<string, any>) {
                file.progress = Math.round(((offset || 0) / file.size) * 100);
                if (file.progress > 100) {
                    file.progress = 100;
                }

                if (fnFileProgressUpdate) {
                    fnFileProgressUpdate(file);
                }

                let sessionId = result.sessionId;

                if (!endOfFile) {
                    if (seek) seek(sessionId);
                } else {
                    uploadCompleteDeferred(file);
                    if (fnFileUploadCompletedEvent) {
                        fnFileUploadCompletedEvent(file);
                    }
                }
            },
            // @ts-ignore
            function (data: Record<any, any>, status: number) {
                file.error = { data: data, status: status };
                errorHandler(file);
            },
        );
    }

    function ReadFile(file: Record<string, any>, uploadCompleteDeferred: (file: Record<string, any>) => void) {
        //letiable declaration
        let offset = 0;
        let _chunkSize = chunkSize;

        // metadata for the uploaded file
        let reader = new FileReader();
        file.progress = 0;
        // file.error;
        // file.sessionId;
        let resizeFile =
            maximumPixelSizeBeforeDownscale && file.type.includes('image');

        function seek(sessionId?: string) {
            if (resizeFile) {
                // @ts-ignore
                reader.readAsDataURL(file);
            } else {
                if (sessionId) {
                    //for chunked uploads you need a session id from the previous upload
                    file.sessionId = sessionId;
                }
                let slice = file.slice(offset, offset + _chunkSize);
                reader.readAsArrayBuffer(slice);
            }
        }

        reader.onload = function (e) {
            if (resizeFile) {
                resizeIfNeeded(file, uploadCompleteDeferred, e);
                return;
            } else {
                //read the buffer result
                let res = new Uint8Array(e.target?.result as ArrayBuffer);

                // API call for saving of the chunks
                //move the offset by the read chunk

                offset += _chunkSize;
                if (file.size > _chunkSize) {
                    file.chunked = true;
                }

                //after initial small chunk resume chunking as normal
                if (originalChunkSize !== _chunkSize) {
                    _chunkSize = originalChunkSize;
                }

                let endOfFile = offset >= file.size;
                //let array = Array.from(res);

                //@ts-ignore
                res = Array.from(res);

                //the file, with the chunk buffer, is end of file
                let input = uploadServiceInputGeneratorFn(file, res, endOfFile);
                UploadFile(
                    input,
                    uploadCompleteDeferred,
                    endOfFile,
                    file,
                    seek,
                    offset
                );
            }
        };

        reader.onerror = function (_e) {
            // deferred.reject(e);
            //todo
        };

        seek();

        
    }

    function resizeIfNeeded(file: Record<string, any>, uploadCompleteDeferred: (file: Record<string, any>) => void, e: Record<string, any>) {
        let img = new Image();
        img.src = e.target.result;

        img.decode()
            .then(
                () => {
                    let pixelSize = img.width * img.height;
                    if (pixelSize < maximumPixelSizeBeforeDownscale) {
                        let res = Array.from(
                            convertDataURIToBinary(e.target.result)
                        );
                        let input = uploadServiceInputGeneratorFn(
                            file,
                            // @ts-ignore
                            res,
                            true
                        );
                        UploadFile(
                            input,
                            uploadCompleteDeferred,
                            true,
                            file,
                            undefined,
                            undefined
                        );
                        return;
                    }
                    let S = img.width / img.height;
                    let L = img.height / img.width;

                    let newHeight = Math.round(
                        Math.sqrt(maximumPixelSizeBeforeDownscale / S)
                    );
                    let newWidth = Math.round(
                        Math.sqrt(maximumPixelSizeBeforeDownscale / L)
                    );

                    let canvas = document.createElement('canvas');
                    canvas.width = newWidth;
                    canvas.height = newHeight;

                    let ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, newWidth, newHeight);

                    let res = Array.from(
                        convertDataURIToBinary(canvas.toDataURL(file.type))
                    );

                    // @ts-ignore
                    let input = uploadServiceInputGeneratorFn(file, res, true);
                    UploadFile(
                        input,
                        uploadCompleteDeferred,
                        true,
                        file,
                        undefined,
                        undefined
                    );
                },
                function () {
                    sendOriginalImage(e, file, uploadCompleteDeferred);
                }
            )
            .catch(function () {
                sendOriginalImage(e, file, uploadCompleteDeferred);
            });
    }

    function sendOriginalImage(e: Record<string, any>, file: Record<string, any>, uploadCompleteDeferred: (file: Record<string, any>) => void) {
        let res = Array.from(convertDataURIToBinary(e.target.result));
        // @ts-ignore
        let input = uploadServiceInputGeneratorFn(file, res, true);
        UploadFile(input, uploadCompleteDeferred, true, file, undefined, undefined);
    }

    function convertDataURIToBinary(dataURI: string) {
        let BASE64_MARKER = ';base64,';
        let base64Index = dataURI.indexOf(BASE64_MARKER) + BASE64_MARKER.length;
        let base64 = dataURI.substring(base64Index);
        let raw = atob(base64);
        let rawLength = raw.length;
        let array = new Uint8Array(new ArrayBuffer(rawLength));

        for (let i = 0; i < rawLength; i++) {
            array[i] = raw.charCodeAt(i);
        }
        return array;
    }

    return {
        UploadFileFunction,
    };
}
